/**
 * Scene Timeline panel — the full-screen view of the studio's real,
 * time-based Premiere-style timeline (timeline.md /
 * mockups/timeline-premiere.png).
 *
 * The same `PremiereTimeline` editor powers the persistent bottom dock
 * (`Timeline.ts`); this panel hosts it at full size for `[OPEN:timeline]`
 * directives and the studio tour. It's the EXPORT surface: a real,
 * exportable, editable cut that visibly BUILDS UP IN LAYERS as the
 * pipeline fills — subtitles → dialogue audio → storyboard filmstrip →
 * final video → SFX + background score. Every clip is click-to-edit.
 */

import { BasePanel } from "./BasePanel";
import type { PanelManager } from "../PanelManager";
import type { StudioStore } from "../StudioStore";
import { PremiereTimeline } from "../PremiereTimeline";
import "../styles/timeline.css";

interface TimelinePanelOptions {
  store: StudioStore;
  panelManager: PanelManager;
}

export class TimelinePanel extends BasePanel {
  private readonly store: StudioStore;
  private readonly panelManager: PanelManager;
  private timeline: PremiereTimeline | null = null;

  constructor(options: TimelinePanelOptions) {
    super();
    this.store = options.store;
    this.panelManager = options.panelManager;
  }

  protected buildRootElement(): HTMLElement {
    const root = document.createElement("div");
    root.className = "tl-root";
    this.timeline = new PremiereTimeline({
      store: this.store,
      panelManager: this.panelManager,
      variant: "panel",
    });
    root.appendChild(this.timeline.getElement());
    return root;
  }

  protected onMounted(): void {
    this.timeline?.start();
  }

  protected onShown(): void {
    // Re-layout now that the panel has its real on-screen width.
    this.timeline?.refresh();
  }

  protected onBeforeDestroy(): void {
    this.timeline?.destroy();
    this.timeline = null;
  }
}
